import React, { useState, useEffect } from 'react';
import { Category } from '../../services/userService';
import { useCategories } from '../../context/CategoriesContext';


const CategoryManager: React.FC = () => {
  const { categories, loading, error, updateCategories } = useCategories();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState('');
  const [newCategoryName, setNewCategoryName] = useState('');
  const [selectedColor, setSelectedColor] = useState('slate');

  const availableColors = [
    { name: 'slate', bg: 'bg-slate-500', text: 'text-white', sample: 'bg-slate-500' },
    { name: 'gray', bg: 'bg-gray-500', text: 'text-white', sample: 'bg-gray-500' },
    { name: 'red', bg: 'bg-red-500', text: 'text-white', sample: 'bg-red-500' },
    { name: 'orange', bg: 'bg-orange-500', text: 'text-white', sample: 'bg-orange-500' },
    { name: 'amber', bg: 'bg-amber-500', text: 'text-white', sample: 'bg-amber-500' },
    { name: 'yellow', bg: 'bg-yellow-400', text: 'text-gray-900', sample: 'bg-yellow-400' },
    { name: 'lime', bg: 'bg-lime-500', text: 'text-white', sample: 'bg-lime-500' },
    { name: 'green', bg: 'bg-green-500', text: 'text-white', sample: 'bg-green-500' },
    { name: 'emerald', bg: 'bg-emerald-500', text: 'text-white', sample: 'bg-emerald-500' },
    { name: 'teal', bg: 'bg-teal-500', text: 'text-white', sample: 'bg-teal-500' },
    { name: 'cyan', bg: 'bg-cyan-500', text: 'text-white', sample: 'bg-cyan-500' },
    { name: 'blue', bg: 'bg-blue-500', text: 'text-white', sample: 'bg-blue-500' },
    { name: 'indigo', bg: 'bg-indigo-500', text: 'text-white', sample: 'bg-indigo-500' },
    { name: 'violet', bg: 'bg-violet-500', text: 'text-white', sample: 'bg-violet-500' },
    { name: 'purple', bg: 'bg-purple-500', text: 'text-white', sample: 'bg-purple-500' },
    { name: 'pink', bg: 'bg-pink-500', text: 'text-white', sample: 'bg-pink-500' },
  ];

  const saveCategories = async (newCategories: Category[]) => {
    await updateCategories(newCategories);
  };

  const handleEdit = (category: Category) => {
    setEditingId(category.id);
    setEditingName(category.name);
    setSelectedColor(category.color);
  };

  const handleSave = async () => {
    if (editingId && editingName.trim()) {
      const updatedCategories = categories.map(cat =>
        cat.id === editingId
          ? { ...cat, name: editingName, color: selectedColor, textColor: selectedColor }
          : cat
      );
      await saveCategories(updatedCategories);
      setEditingId(null);
      setEditingName('');
    }
  };

  const handleDelete = async (id: string) => {
    if (categories.length > 1 && window.confirm('Are you sure you want to delete this category?')) {
      const updatedCategories = categories.filter(cat => cat.id !== id);
      await saveCategories(updatedCategories);
    }
  };

  const handleAdd = async () => {
    if (newCategoryName.trim()) {
      const newCategory: Category = {
        id: newCategoryName.toLowerCase().replace(/\s+/g, '-'),
        name: newCategoryName,
        color: selectedColor,
        textColor: selectedColor,
      };
      await saveCategories([...categories, newCategory]);
      setNewCategoryName('');
      setSelectedColor('slate');
    }
  };

  const getColorClasses = (color: string) => {
    const colorData = availableColors.find(c => c.name === color);
    return colorData ? `${colorData.bg} ${colorData.text}` : 'bg-gray-500 text-white';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div>
      <h2 className="text-lg font-semibold text-gray-900 mb-4">Manage Categories</h2>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}
      
      {/* Existing Categories */}
      <div className="space-y-2 mb-6">
        {categories.map(category => (
          <div key={category.id} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
            {editingId === category.id ? (
              <div className="flex items-center gap-2 flex-1">
                <input
                  type="text"
                  value={editingName}
                  onChange={(e) => setEditingName(e.target.value)}
                  className="px-2 py-1 border border-gray-300 rounded text-sm flex-1"
                  autoFocus
                />
                <select
                  value={selectedColor}
                  onChange={(e) => setSelectedColor(e.target.value)}
                  className="px-2 py-1 border border-gray-300 rounded text-sm"
                >
                  {availableColors.map(color => (
                    <option key={color.name} value={color.name}>{color.name}</option>
                  ))}
                </select>
                <div className={`w-20 px-2 py-1 rounded text-xs text-center ${getColorClasses(selectedColor)}`}>
                  Preview
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-3">
                <span className={`px-2 py-1 rounded text-sm ${getColorClasses(category.color)}`}>
                  {category.name}
                </span>
              </div>
            )}
            
            <div className="flex gap-2">
              {editingId === category.id ? (
                <>
                  <button
                    onClick={handleSave}
                    className="text-green-600 hover:text-green-700 text-sm"
                  >
                    Save
                  </button>
                  <button
                    onClick={() => {
                      setEditingId(null);
                      setEditingName('');
                    }}
                    className="text-gray-600 hover:text-gray-700 text-sm"
                  >
                    Cancel
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={() => handleEdit(category)}
                    className="text-blue-600 hover:text-blue-700 text-sm"
                  >
                    Edit
                  </button>
                  {categories.length > 1 && (
                    <button
                      onClick={() => handleDelete(category.id)}
                      className="text-red-600 hover:text-red-700 text-sm"
                    >
                      Delete
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Add New Category */}
      <div className="border-t pt-4">
        <h3 className="text-sm font-medium text-gray-900 mb-3">Add New Category</h3>
        <div className="flex items-center gap-2">
          <input
            type="text"
            value={newCategoryName}
            onChange={(e) => setNewCategoryName(e.target.value)}
            placeholder="Category name"
            className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm"
          />
          <select
            value={selectedColor}
            onChange={(e) => setSelectedColor(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md text-sm"
          >
            {availableColors.map(color => (
              <option key={color.name} value={color.name}>{color.name}</option>
            ))}
          </select>
          <div className={`w-24 px-3 py-2 rounded text-sm text-center ${getColorClasses(selectedColor)}`}>
            Preview
          </div>
          <button
            onClick={handleAdd}
            className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700"
          >
            Add
          </button>
        </div>
      </div>

      {/* Color Palette Reference */}
      <div className="mt-6 pt-4 border-t">
        <h3 className="text-sm font-medium text-gray-900 mb-3">Available Colors</h3>
        <div className="grid grid-cols-8 gap-2">
          {availableColors.map(color => (
            <button
              key={color.name}
              onClick={() => setSelectedColor(color.name)}
              className={`h-8 rounded ${color.sample} hover:opacity-80 transition-opacity`}
              title={color.name}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default CategoryManager;